import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {
    CertificateApi,
    CertificateSelectionApi,
    CertificateStore,
    CertificateValidationApi,
    type CertificateValidationResult,
    Expired,
    Match,
    MissingKeyAlias,
    type X509CertificateDetails
} from "components/src/api/admin/generated";
import {CertificateFilter} from "components/src/certificate/CertificateFilter";
import {CertificateRow} from "components/src/certificate/CertificateRow";
import {CertificateTableHeader} from "components/src/certificate/CertificateTableHeader";
import {SearchField} from "components/src/certificate/SearchField";
import {ActionsDropdown} from "components/src/common/ActionsDropdown";
import {Pager} from "components/src/common/Pager";
import {RenderAlert} from "components/src/common/RenderAlert";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {SaveChangesRef} from "components/src/common/SaveChangesRef";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {SettingsLevel} from "components/src/settings/SettingsUtils";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {Dispatch, forwardRef, SetStateAction, useCallback, useEffect, useImperativeHandle, useState} from 'react'
import {Dropdown} from "react-bootstrap";
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import {useNavigate} from "react-router-dom";

interface SigningCertificateProps {
    settingsLevel : SettingsLevel
    settingsLevelSubSelector : string
    setShouldSave: Dispatch<SetStateAction<boolean>>
    setSaving : Dispatch<SetStateAction<boolean>>
}

const certificateApi = new CertificateApi(ApiConfig.config());
const certificateValidationApi = new CertificateValidationApi(ApiConfig.config());
const certificateSelectionApi = new CertificateSelectionApi(ApiConfig.config());

const certificateStore : CertificateStore = CertificateStore.Certificates;

// TODO: add saving param
export const SigningCertificate = forwardRef<SaveChangesRef | undefined, SigningCertificateProps> ((({
                settingsLevel,
                settingsLevelSubSelector,
                setShouldSave,
                setSaving} : SigningCertificateProps, ref) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * All the filtered certificates
     */
    const [filteredCertificates, setFilteredCertificates] = useState<X509CertificateDetails[]>([]);
    /**
     * The total number of filtered certificates
     */
    const [filteredCertificateCount, setFilteredCertificateCount] = useState<number>(0);
    /**
     * The currently used signing certificate
     */
    const [signingCertificate, setSigningCertificate] = useState<X509CertificateDetails>();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the current page
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of certificates to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(20);
    /**
     * The selected signing certificate
     */
    const [selectedThumbrint, setSelectedThumbrint] = useState<string>();
    /**
     * The field to search on
     */
    const [searchField, setSearchField] = useState<SearchField>(SearchField.Email);
    /**
     * The field which is searched on
     */
    const [activeSearchValue, setActiveSearchValue] = useState<string | undefined>(settingsLevel === SettingsLevel.User ? settingsLevelSubSelector : "");
    /**
     * The field of the search box. This value will be copied to the activeSearchValue when apply is clicked
     */
    const [searchValue, setSearchValue] = useState<string | undefined>(activeSearchValue);
    /**
     * Incremented whenever the data should be reloaded from the back-end. A counter is used instead of a boolean
     * because every increment is a new value, so a reload is never dropped and the load effects run exactly once
     * per reload request
     */
    const [reloadSigningCertificateCounter, setReloadSigningCertificateCounter] = useState<number>(0);
    /**
     * Forces the data to be reloaded from the back-end
     */
    const forceReloadSigningCertificate = useCallback(() => setReloadSigningCertificateCounter(counter => counter + 1), []);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    /**
     * Override the forwarded ref with the saveSettings method to allow the parent to save the modified settings
     */
    useImperativeHandle(ref, () => {
        return {
            saveChanges(){
                save();
            }
        }
    });

    /**
     * Load the number of certificates that match the active filter
     */
    useEffect(() => {
        logger.debug("Getting filtered certificate count");

        // Note: do not reset errorMessage because otherwise the message might get lost when re-rendering
        //       The error message can be closed by clicking the close icon

        let loadCertificateCountCallback;

        if ((activeSearchValue??"").length > 0)
        {
            switch (searchField)
            {
                case SearchField.Email:
                    loadCertificateCountCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByEmailCount(
                            certificateStore, `%${activeSearchValue??""}%`,
                            Match.Like, Expired.UnexpiredOnly, MissingKeyAlias.NotAllowed);
                    };
                    break;
                case SearchField.Subject:
                    loadCertificateCountCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesBySubjectCount(
                            certificateStore, `%${activeSearchValue??""}%`,
                            Expired.UnexpiredOnly, MissingKeyAlias.NotAllowed);
                    };
                    break;
                case SearchField.Issuer:
                    loadCertificateCountCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByIssuerCount(
                            certificateStore, `%${activeSearchValue??""}%`,
                            Expired.UnexpiredOnly, MissingKeyAlias.NotAllowed);
                    };
                    break;
            }
        }
        else {
            loadCertificateCountCallback = async () => {
                return await certificateApi.certificateControllerGetCertificatesCount(certificateStore,
                    Expired.UnexpiredOnly, MissingKeyAlias.NotAllowed);
            };
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        loadCertificateCountCallback()
            .then(response => {
                if (superseded) {
                    return;
                }

                setFilteredCertificateCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting filtered certificates count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })

        return () => {
            superseded = true;
        }
    }, [navigate,
        activeSearchValue,
        searchField,
        setSpinnerStateCounter,
        t]);

    /**
     * Load all certificates matching the filter
     */
    useEffect(() => {
        logger.debug("Getting filtered certificates");

        let loadCertificatesCallback;

        if ((activeSearchValue??"").length > 0)
        {
            switch (searchField)
            {
                case SearchField.Email:
                    loadCertificatesCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByEmail(
                            certificateStore, `%${activeSearchValue??""}%`, Match.Like,
                            Expired.UnexpiredOnly, MissingKeyAlias.NotAllowed,
                            (currentPage - 1) * rowsPerPage, rowsPerPage);
                    };
                    break;
                case SearchField.Subject:
                    loadCertificatesCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesBySubject(
                            certificateStore, `%${activeSearchValue??""}%`,
                            Expired.UnexpiredOnly, MissingKeyAlias.NotAllowed,
                            (currentPage - 1) * rowsPerPage, rowsPerPage);
                    };
                    break;
                case SearchField.Issuer:
                    loadCertificatesCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByIssuer(
                            certificateStore, `%${activeSearchValue??""}%`,
                            Expired.UnexpiredOnly, MissingKeyAlias.NotAllowed,
                            (currentPage - 1) * rowsPerPage, rowsPerPage);
                    };
                    break;
            }
        }
        else {
            loadCertificatesCallback = async () => {
                return await certificateApi.certificateControllerGetCertificates(certificateStore,
                    Expired.UnexpiredOnly, MissingKeyAlias.NotAllowed,
                    (currentPage - 1) * rowsPerPage, rowsPerPage);
            };
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        loadCertificatesCallback().then(response => {
            if (superseded) {
                return;
            }

            setFilteredCertificates(response.data);
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting filtered certificates", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })

        return () => {
            superseded = true;
        }
    }, [navigate,
        activeSearchValue,
        searchField,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        t
    ]);

    /**
     * Load the signing certificate
     */
    useEffect(() => {
        logger.debug("Getting signing certificate");

        let loadCertificateCallback;

        switch (settingsLevel)
        {
            case SettingsLevel.User:
                loadCertificateCallback = async () => {
                    return await certificateSelectionApi.certificateSelectionControllerGetSigningCertificateForUser(
                        settingsLevelSubSelector);
                };
                break;
            case SettingsLevel.Domain:
                loadCertificateCallback = async () => {
                    return await certificateSelectionApi.certificateSelectionControllerGetSigningCertificateForDomain(
                        settingsLevelSubSelector);
                };
                break;
        }

        if (loadCertificateCallback === undefined) {
            throw new Error("loadCertificateCallback is undefined");
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        loadCertificateCallback().then(response =>
        {
            if (superseded) {
                return;
            }

            let certificate : X509CertificateDetails | undefined = response.data;
            // for some reason, openapi returns an empty certificate if the back-end returns null
            certificate = certificate.thumbprint !== undefined ? certificate : undefined;

            setSigningCertificate(certificate);

            setSelectedThumbrint(certificate !== undefined ? certificate.thumbprint : undefined);
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error getting signing certificate", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);

            if (superseded) {
                return;
            }

            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })

        return () => {
            superseded = true;
        }
    }, [navigate,
        setSpinnerStateCounter,
        settingsLevel,
        settingsLevelSubSelector,
        reloadSigningCertificateCounter,
        t
    ]);

    /**
     * Saves changes. This called by the parent component via a useImperativeHandle (see above)
     */
    const save = () =>
    {
        if (selectedThumbrint === undefined) {
            throw new Error("selectedCertificate === undefined")
        }

        saveSigningCertificate(selectedThumbrint);
    }

    /**
     * Saves changes. This called by the parent component via a useImperativeHandle (see above)
     */
    const saveSigningCertificate = (thumbprint : string | undefined) =>
    {
        logger.debug("saving selected certificate", selectedThumbrint)

        let saveCertificateCallback;

        switch (settingsLevel)
        {
            case SettingsLevel.User:
                saveCertificateCallback = async () => {
                    return await certificateSelectionApi.certificateSelectionControllerSetSigningCertificateForUser(
                        settingsLevelSubSelector, thumbprint);
                };
                break;
            case SettingsLevel.Domain:
                saveCertificateCallback = async () => {
                    return await certificateSelectionApi.certificateSelectionControllerSetSigningCertificateForDomain(
                        settingsLevelSubSelector, thumbprint);
                };
                break;
        }

        if (saveCertificateCallback === undefined) {
            throw new Error("saveCertificateCallback is undefined");
        }

        // tell the parent that this component is saving (to show a spinner)
        setSaving(true);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        saveCertificateCallback().then(() => {
            setShouldSave(false);
            // force a reload of the signing certificate
            forceReloadSigningCertificate();
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error setting signing certificate", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() =>
        {
            setSaving(false);

            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }

    const resetSigningCertificate = () => {
        saveSigningCertificate(undefined);
    }

    /**
     * Called when a certificate is selected (i.e., when a radio button is selected)
     */
    const onSelectCertificate = (thumbprint : string) =>
    {
        setSelectedThumbrint(thumbprint);

        // if the currently selected signing certificate is different from the new, save is required
        setShouldSave(thumbprint !== signingCertificate?.thumbprint);
    }

    /**
     * Checks the validity of a certificate for signing using the certificate validation API.
     *
     * NOTE: this must be wrapped in a useCallback call to prevent unlimited rendering because the spinner state is set
     */
    const checkCertificateValidity = (useCallback((certificate : X509CertificateDetails, setCertificateValidationResult : Dispatch<SetStateAction<CertificateValidationResult | undefined>>) =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        certificateValidationApi.certificateValidationControllerValidateCertificateForSigning(certificateStore, certificate.thumbprint)
            .then((response) =>
            {
                const validationResult : CertificateValidationResult = response.data;

                setCertificateValidationResult(validationResult);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error certificate validation result", err);
                setCertificateValidationResult(undefined);
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [setSpinnerStateCounter]));

    function getCertificates(): X509CertificateDetails[]
    {
        const certificates : X509CertificateDetails[] = [];

        if (signingCertificate !== undefined && signingCertificate !== null) {
            certificates.push(signingCertificate);
        }

        // add all filtered certificates but not if the certificate is the signing certificate
        certificates.push(...filteredCertificates.filter(cert => cert.thumbprint !== signingCertificate?.thumbprint));

        return certificates;
    }

    return (
        <>
            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row>
                <div className="d-flex flex-row align-items-center mb-4">
                    <div className="d-flex flex-fill justify-content-end align-items-center">
                        <div className="d-xl-flex">
                            <CertificateFilter searchField={searchField}
                                               setSearchField={setSearchField}
                                               searchValue={searchValue}
                                               setSearchValue={setSearchValue}
                                               activeSearchValue={activeSearchValue}
                                               setActiveSearchValue={setActiveSearchValue}
                            />

                            <div className="ms-4"/>

                            <ActionsDropdown>
                                <Dropdown.Item onClick={() => resetSigningCertificate()}>
                                    {t("certificates.reset-signing-certificate")}
                                </Dropdown.Item>
                            </ActionsDropdown>
                        </div>
                    </div>
                </div>
            </Row>

            <Row className="h-100 w-100 overflow-auto">
                <Col>
                    <Table className="sticky sticky-x" bordered hover>
                        <CertificateTableHeader/>
                        <tbody>
                        {getCertificates().map((certificate) => (
                            <CertificateRow key={certificate.thumbprint} certificate={certificate} checkCertificateValidityCallback={checkCertificateValidity}>
                                {/* Add radio button to first column */}
                                <Form.Check
                                    name="signing-certificate"
                                    type="radio"
                                    className="me-0"
                                    value={certificate.thumbprint}
                                    checked={selectedThumbrint === certificate.thumbprint}
                                    onChange={(e) => onSelectCertificate(e.target.value)}
                                    inline
                                />
                            </CertificateRow>
                        ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>

            <div className="d-flex justify-content-between align-items-baseline mt-2 mb-2">
                <div>
                    <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage}/>
                </div>
                <Pager itemCount={filteredCertificateCount ?? 0}
                       currentPage={currentPage}
                       setCurrentPage={setCurrentPage}
                       maxItemsPerPage={rowsPerPage}
                />
            </div>
        </>
    );
}))

// see https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/display-name.md why this is needed
SigningCertificate.displayName="SigningCertificate"
